import logger from '@/utils/logger';

import { TRPCClientError } from '@trpc/client';

import { buildLoginRedirectPath } from './authRedirect';

const AUTH_TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
/**
 * Where the browser must go to end the WorkOS session, kept for the moment
 * the session ends without anyone pressing "log out".
 *
 * The URL has to be minted by an authenticated call, because it carries the
 * session id out of the caller's own verified token. An idle timeout or a
 * rejected refresh has no usable token left by the time it needs this, so the
 * URL is fetched while the session is healthy and read back afterwards.
 */
const WORKOS_LOGOUT_URL_KEY = 'workosLogoutUrl';
const AUTH_TOKEN_AUDIENCE = 'teo-property-management';
/**
 * A session Teo issues to itself: an administrator impersonating someone, and
 * the spec harness standing a user up without a password. Opaque here — it
 * names a user id the server resolves, and carries nothing this side can read.
 */
const INTERNAL_TOKEN_AUDIENCE = 'teo-internal-session';

type AuthTokens = {
  token: string;
  refreshToken?: string;
};

type AuthLogMetadata = Record<string, boolean | number | string | null | undefined>;
export type AuthSessionInvalidatedDetail = {
  reason: string;
  metadata: AuthLogMetadata;
  navigateToLogin: boolean;
};
type DecodedAuthTokenPayload = Record<string, unknown> & {
  aud?: unknown;
  exp?: unknown;
  sub?: unknown;
};

export const AUTH_SESSION_INVALIDATED_EVENT = 'teo:auth-session-invalidated';

function hasLocalStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function parseAuthTokenPayload(token: string): DecodedAuthTokenPayload | null {
  try {
    const [, payloadSegment] = token.split('.');
    if (!payloadSegment) {
      return null;
    }

    const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');

    return JSON.parse(atob(padded)) as DecodedAuthTokenPayload;
  } catch {
    return null;
  }
}

/**
 * Which credential is in storage.
 *
 * A legacy token is one we minted. A WorkOS token is the vendor's, and carries a
 * subject and nothing else about who that is. The browser reads the user from
 * neither: it asks the server, which answers from the row.
 *
 * `workos` is recognised by the claims a token *has* rather than by its issuer,
 * because a custom AuthKit auth domain replaces the issuer and the frontend has
 * no way to know what it was configured to. Absence of `aud` is the reliable
 * half: WorkOS access tokens carry no audience at all, and ours always do.
 */
export type AuthTokenKind = 'legacy' | 'workos' | 'internal' | 'unusable';

export function classifyAuthToken(token: string): AuthTokenKind {
  const payload = parseAuthTokenPayload(token);
  if (!payload) {
    return 'unusable';
  }
  if (payload.aud === AUTH_TOKEN_AUDIENCE) {
    return 'legacy';
  }
  if (payload.aud === INTERNAL_TOKEN_AUDIENCE) {
    return 'internal';
  }
  if (
    payload.aud === undefined &&
    typeof payload.sub === 'string' &&
    typeof payload.exp === 'number'
  ) {
    return 'workos';
  }
  return 'unusable';
}

/**
 * Whether a token is worth attaching to a request. Not a verification — the
 * frontend holds no keys and the backend is the authority. This only avoids
 * spending a round trip on something already known to be junk.
 */
export function isUsableAuthToken(token: string): boolean {
  return classifyAuthToken(token) !== 'unusable';
}

export function hasExpectedAuthAudience(token: string): boolean {
  const payload = parseAuthTokenPayload(token);
  return payload?.aud === AUTH_TOKEN_AUDIENCE;
}

/**
 * The access token this tab last stored or took from storage.
 *
 * Tabs share the tokens, and a tab can decide on its own that a session is over.
 * What it may then remove is its own session's token, and not one that someone
 * has stored since.
 *
 * A refresh in a sibling tab stores a new token, and this tab is then not its
 * owner. If the activity stamp is lost after that, this tab ends its own session
 * and leaves the tokens. The app never removes the stamp and leaves the tokens, so
 * only something outside it reaches that path: developer tools, an extension, or a
 * partial clear of storage.
 */
let ownAuthToken: string | null = null;

export function storeAuthTokens(tokens: AuthTokens, reason: string): void {
  if (!hasLocalStorage()) return;

  window.localStorage.setItem(AUTH_TOKEN_KEY, tokens.token);
  ownAuthToken = tokens.token;
  if (tokens.refreshToken) {
    window.localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  } else {
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  logger.info(
    {
      reason,
      hasRefreshToken: Boolean(tokens.refreshToken),
    },
    'Stored auth session tokens',
  );
}

export function rememberWorkosLogoutUrl(url: string): void {
  if (hasLocalStorage()) {
    window.localStorage.setItem(WORKOS_LOGOUT_URL_KEY, url);
  }
}

/**
 * Reads the remembered logout URL and forgets it, so one invalidation cannot
 * send the browser to WorkOS twice.
 *
 * Deliberately not cleared by `clearAuthTokens`: clearing runs first on every
 * path that ends a session, and this is needed after it.
 */
export function takeWorkosLogoutUrl(): string | null {
  if (!hasLocalStorage()) {
    return null;
  }
  const url = window.localStorage.getItem(WORKOS_LOGOUT_URL_KEY);
  window.localStorage.removeItem(WORKOS_LOGOUT_URL_KEY);
  return url;
}

export function clearAuthTokens(reason: string, metadata: AuthLogMetadata = {}): void {
  if (hasLocalStorage()) {
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  logger.warn(
    {
      reason,
      ...metadata,
    },
    'Cleared auth session tokens',
  );
}

export function invalidateAuthSession(
  reason: string,
  metadata: AuthLogMetadata = {},
  options: { clearTokens?: boolean; navigateToLogin?: boolean } = {},
): void {
  if (options.clearTokens ?? true) {
    clearAuthTokens(reason, metadata);
  }

  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<AuthSessionInvalidatedDetail>(AUTH_SESSION_INVALIDATED_EVENT, {
      detail: {
        reason,
        metadata,
        navigateToLogin: options.navigateToLogin ?? true,
      },
    }),
  );
}

export function clearAuthTokensIfRefreshTokenMatches(
  expectedRefreshToken: string,
  reason: string,
  metadata: AuthLogMetadata = {},
): boolean {
  if (!hasLocalStorage()) {
    return false;
  }

  const currentRefreshToken = window.localStorage.getItem(REFRESH_TOKEN_KEY);
  if (currentRefreshToken !== expectedRefreshToken) {
    logger.info(
      {
        reason,
        refreshTokenRotatedElsewhere: true,
        ...metadata,
      },
      'Skipped clearing auth session tokens because refresh token changed',
    );
    return false;
  }

  clearAuthTokens(reason, metadata);
  return true;
}

export function redirectToLogin(reason: string, metadata: AuthLogMetadata = {}): void {
  logger.warn(
    {
      reason,
      path: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
      ...metadata,
    },
    'Redirecting user to login',
  );

  if (typeof window !== 'undefined') {
    window.location.href = buildLoginRedirectPath(
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
  }
}

export function isConfirmedSessionFailure(error: unknown): boolean {
  return error instanceof TRPCClientError && error.data?.httpStatus === 401;
}

/** Whether an auth token is in localStorage now. */
export function hasAuthToken(): boolean {
  return hasLocalStorage() && window.localStorage.getItem(AUTH_TOKEN_KEY) !== null;
}

/**
 * Whether a storage event is about the auth token: that key changed, or all of
 * localStorage was cleared. Another storage area, sessionStorage included, holds
 * none of ours, and its `clear` reports the same null key.
 *
 * It does not say whether the token was removed or replaced. Read `hasAuthToken`
 * for that: by the time a tab handles the event, a newer token may be stored.
 */
export function isAuthTokenStorageEvent(event: Pick<StorageEvent, 'key' | 'storageArea'>): boolean {
  return (
    hasLocalStorage() &&
    event.storageArea === window.localStorage &&
    (event.key === null || event.key === AUTH_TOKEN_KEY)
  );
}

/** Takes the token in storage as this tab's own, for a session this tab begins to hold. */
export function adoptStoredAuthToken(): void {
  ownAuthToken = hasLocalStorage() ? window.localStorage.getItem(AUTH_TOKEN_KEY) : null;
}

/**
 * Clears the tokens only while the stored token is still this tab's own, as
 * `clearAuthTokensIfRefreshTokenMatches` does for a refresh. A different token is a
 * newer session that another tab stored, and it is left alone.
 */
export function clearAuthTokensIfOwn(reason: string, metadata: AuthLogMetadata = {}): boolean {
  if (!hasLocalStorage()) {
    return false;
  }

  const stored = window.localStorage.getItem(AUTH_TOKEN_KEY);
  if (stored === null || stored !== ownAuthToken) {
    logger.info(
      { reason, ...metadata },
      "Skipped clearing auth session tokens because they are not this tab's own",
    );
    return false;
  }

  clearAuthTokens(reason, metadata);
  return true;
}
